/**
 * Territory — Phase 1.
 *
 * Operator doctrine (May 25, 2026):
 *   • Visible PROOF, not abstract graphs. ZERO synthetic data.
 *   • Calm empty state when no postcodes captured yet.
 *   • Timeline switcher (day/week/month) wired to backend (echo only
 *     in Phase 1 — future phases will render density per window).
 *   • Standalone instructor / PDI scope only. School-owner deferred.
 *   • Phase 1 surfaces pupil markers + counts. Density, isolation
 *     zones, utilisation overlays come in Phase 2 once real data
 *     accrues.
 *
 * No fake density. No synthetic postcodes. No seeded map markers.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Upload, ChevronLeft, RefreshCcw, Sparkles, AlertCircle,
} from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import TerritoryBulkPasteModal from "../components/territory/TerritoryBulkPasteModal";

const API = process.env.REACT_APP_BACKEND_URL;

// Geographic centre of the UK (Whitwell, Derbyshire). Used ONLY as
// the initial map fitBounds fallback when no pupil markers exist.
// This is a render-time map centroid — NOT pupil data.
const UK_CENTRE = { lat: 54.0, lng: -2.0 };
const UK_ZOOM = 6;

const WINDOWS = [
  { id: "day",   label: "Day"   },
  { id: "week",  label: "Week"  },
  { id: "month", label: "Month" },
];

export default function Territory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState("week");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/territory/overview?window=${window}`,
        { withCredentials: true },
      );
      setData(r.data);
    } catch {
      toast.error("Couldn't load territory data.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const pupils = data?.pupils || [];
  const ready = data?.ready_for_map === true;

  // Compute map bounds from real pupil coords. NEVER synthesise.
  const bounds = pupils.length > 0
    ? pupils.map((p) => [p.lat, p.lng])
    : null;

  return (
    <div data-testid="territory-page" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link
            to="/"
            data-testid="territory-back"
            className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">Territory</p>
            <p className="text-[11px] text-slate-500 leading-tight">
              Phase 1 · real postcodes only · UK postcode-area centroids
            </p>
          </div>
          <button
            type="button"
            data-testid="territory-bulk-open"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <Upload className="w-3.5 h-3.5" />
            Bulk-paste postcodes
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-5 space-y-4">
        {/* Summary tiles */}
        <div data-testid="territory-summary-row" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { key: "total_pupils", label: "Total pupils" },
            { key: "with_postcode", label: "Have postcode" },
            { key: "geocoded", label: "Geocoded" },
            { key: "no_postcode", label: "No postcode" },
          ].map((tile) => (
            <div
              key={tile.key}
              data-testid={`territory-summary-${tile.key}`}
              className="rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-3"
            >
              <p className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">{tile.label}</p>
              <p className="text-2xl font-extrabold text-slate-900 dark:text-white tabular-nums mt-0.5">
                {summary[tile.key] ?? 0}
              </p>
            </div>
          ))}
        </div>

        {/* Timeline switcher */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">View</span>
          <div data-testid="territory-window-switcher" className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                data-testid={`territory-window-${w.id}`}
                onClick={() => setWindow(w.id)}
                className={`px-3 py-1.5 text-xs font-bold ${
                  window === w.id
                    ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="territory-refresh"
            onClick={load}
            disabled={loading}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 disabled:opacity-50"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Map / empty state */}
        {loading ? (
          <div data-testid="territory-loading" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 h-96 flex items-center justify-center text-sm text-slate-500">
            Loading…
          </div>
        ) : !ready ? (
          <div
            data-testid="territory-empty-state"
            className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 text-center"
          >
            <Sparkles className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <p className="text-base font-extrabold text-slate-900 dark:text-white mb-1">
              No geographic data yet
            </p>
            <p className="text-[13px] text-slate-600 dark:text-slate-300 max-w-md mx-auto leading-relaxed">
              Your territory map fills in as you capture pupil postcodes. We don't
              show fake or seeded markers — your map will be honest from day one.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2 max-w-xs mx-auto text-left">
              <div className="rounded-md bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-700 px-2.5 py-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Pupils</p>
                <p className="text-base font-extrabold tabular-nums">{summary.total_pupils ?? 0}</p>
              </div>
              <div className="rounded-md bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-700 px-2.5 py-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Postcodes</p>
                <p className="text-base font-extrabold tabular-nums">{summary.with_postcode ?? 0}</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="territory-empty-bulk-cta"
              onClick={() => setBulkOpen(true)}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[13px] font-bold hover:opacity-90"
            >
              <Upload className="w-4 h-4" />
              Bulk-paste postcodes
            </button>
            <p className="mt-3 text-[11px] text-slate-400">
              Or open a pupil's profile and add their postcode there.
            </p>
          </div>
        ) : (
          <div data-testid="territory-map-shell" className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <MapContainer
              key={pupils.length}
              bounds={bounds}
              center={bounds ? undefined : [UK_CENTRE.lat, UK_CENTRE.lng]}
              zoom={bounds ? undefined : UK_ZOOM}
              scrollWheelZoom
              style={{ height: 480, width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {pupils.map((p) => (
                <CircleMarker
                  key={p.id}
                  center={[p.lat, p.lng]}
                  radius={7}
                  pathOptions={{
                    color: p.avatar_color || "#6366f1",
                    weight: 1.5,
                    fillColor: p.avatar_color || "#6366f1",
                    fillOpacity: 0.65,
                  }}
                >
                  <Tooltip direction="top" offset={[0, -6]}>
                    <span className="text-[11px] font-bold">{p.label}</span>
                    <span className="text-[10px] text-slate-500"> · {p.postcode}</span>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
            <div className="px-3 py-2 text-[10.5px] text-slate-400 border-t border-slate-200 dark:border-slate-800 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" />
              Postcode-area centroid only — not street-level accuracy. Density, isolation zones, and utilisation overlays land in Phase 2.
            </div>
          </div>
        )}
      </main>

      <TerritoryBulkPasteModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={() => { load(); }}
      />
    </div>
  );
}
